import React, { useEffect, useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import { useDispatch } from 'react-redux'

import lang from '@/lang'
import SignerSelection from './signer-selection'
import TextAreaInput from '@/modules/base/components/inputs/textarea-input/textarea-input'
import { DateInput } from '@/modules/base/components/inputs'
import {
  emptySigner,
  addSigner,
  changeDueDate,
  changeMessage,
  changeSigner,
  removeSigner,
  setSigners,
} from '@/redux/ducks/signature/edit'
import { Signer } from '@/modules/signature/types'
import { TODO_COLLABORATION_ID } from '@/modules/base/util/constants'
import { useAppSelector } from '@/redux/store'
import OwnEmailRestriction from '@/modules/base/components/inputs/recipient-email-input/own-email-restriction'
import { MultiSelectTypes } from '@/components/multi-select/constants'

type SignatureFormProps = {
  tenantCollaborationId?: string,
}

const useStyles = makeStyles(() => ({
  inputWrapper: {
    marginTop: 20,
  },
}), { name: 'SignatureForm' })

const SignatureForm = (props: SignatureFormProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { tenantCollaborationId = null } = props
  const { signers, message, dueDate } = useAppSelector(state => state.signature.edit)
  const allowExternalEmails = useAppSelector(state => state.tenant.settings?.allowExternalEmails)
  const { email: userEmail, id: userId } = useAppSelector(state => state.user.info)

  // there can only be one tenantCollaboration per todo
  const collaborationId = useMemo(() => {
    if (tenantCollaborationId && tenantCollaborationId !== TODO_COLLABORATION_ID.INTERNAL) { return tenantCollaborationId }
    return signers.find((signer) => signer.tenantCollaborationId)?.tenantCollaborationId
  }, [signers, tenantCollaborationId])

  useEffect(() => {
    if (!allowExternalEmails) {
      dispatch(setSigners({
        signers: [
          { ...emptySigner, name: userEmail, email: userEmail, id: userId, multiSelectType: MultiSelectTypes.User }],
      }))
    } else if (!signers.length) {
      dispatch(setSigners({ signers: [emptySigner] }))
    }
  }, [])

  const handleMessageChange = (message: string): void => {
    dispatch(changeMessage({ message }))
  }
  const handleDueDateChange = (dueDate: string): void => {
    dispatch(changeDueDate({ dueDate }))
  }
  const handleSignerAdd = (): void => {
    dispatch(addSigner({ signer: emptySigner }))
  }
  const handleSignerDelete = (index: number): void => {
    dispatch(removeSigner({ index }))
  }
  const handleSignerChange = (index: number, signer: Signer): void => {
    dispatch(changeSigner({ index, signer: signer || emptySigner }))
  }

  const ownEmailRestrictionEl = useMemo(() => {
    if (allowExternalEmails) { return null }
    return (
      <OwnEmailRestriction
        label={lang.ownEmailRestriction.label.signature}
        hint={lang.ownEmailRestriction.hint.signature}
      />
    )
  }, [allowExternalEmails])

  return (
    <div>
      {ownEmailRestrictionEl}
      {allowExternalEmails && (
        <SignerSelection
          signers={signers}
          tenantCollaborationId={collaborationId}
          onAdd={handleSignerAdd}
          onDelete={handleSignerDelete}
          onChange={handleSignerChange} />
      )}
      <div className={classes.inputWrapper}>
        <TextAreaInput
          name="content"
          value={message}
          label={lang.signature.form.input.message.label}
          onChange={handleMessageChange}
        />
      </div>
      <div className={classes.inputWrapper}>
        <DateInput
          label={lang.signature.form.input.dueToDate.label}
          name="dueToDate"
          value={dueDate}
          onChange={handleDueDateChange}
        />
      </div>
    </div>
  )
}

export default SignatureForm
